/** Redux Reducers */
import { setSnackbar } from 'shared/store/slices/snackBarSlice';

/** Messages for snackbar notifications */
export const MESSAGES = {
    EMPTY_NAME: 'Please enter a category name.',
    DUPLICATE_CATEGORIES: 'This category name already exists.',
    DUPLICATE_ITEMS: 'This item code already exists.',
    ADD_SUCCESS: 'Successfully added.',
    UPDATE_SUCCESS: 'Successfully updated.',
    UPDATE_ERROR: 'Something went wrong, please try again.',
    FETCH_ERROR: 'Failed to fetch the data, please try again.',
    ADD_ERROR: 'Failed to add, please try again.',
    DELETE_SUCCESS: 'Successfully deleted.',
    USING_FORMULA: 'This formula is in use. Cannot delete.',
    DELETE_ERROR: 'Failed to delete, please try again.',
    EMPTY_FORMULA: 'Please enter a duty name and formula.',
    EMPTY_ITEM: 'Please enter a client, category, item code, and item name.',
};

/** The client this screen updates ERP for — the ERP chart only ever shows JINRO entries. */
export const JINRO_CLIENT_NAME = 'JINRO';

/** Whether ERP can be updated for this entry at all — the server refuses every other client. */
export const isJinroEntry = (row) => {
    const clientName = row?.il_trade_partner?.name ?? row?.il_trade_partner;
    return typeof clientName === 'string' && clientName.trim().toUpperCase() === JINRO_CLIENT_NAME;
};

/**
 * How an operator recognises an entry in a message — the ID column is hidden in this grid.
 * @param {object} row - Duty entry (a grid row, or the identity the server sent back)
 * @returns {string} - Opus Ref, else Entry No., else the raw id
 */
export const dutyEntryLabel = (row) => {
    const id = row?.id ?? row?.il_duty_entry_id;
    return row?.opus_reference || row?.entry_number || `ID ${id}`;
};

/** Reasons an entry is refused before the request is sent — shown next to its Opus Ref. */
export const BULK_ERP_BLOCK_REASONS = {
    NOT_JINRO: `The client is not ${JINRO_CLIENT_NAME}.`,
    MISSING_INVOICE:
        'Needs an invoice date and a fee greater than zero on at least one side (entry and/or trucking).',
};

/**
 * One line per entry the ERP update could not take — "OP-1 (Entry No. E-1): reason".
 *
 * A batch can hold a hundred entries, so the operator is given the reference they read on the
 * screen (Opus Ref / Entry No.) instead of an internal id, together with what to fix.
 * @param {Array<object>} failed - Failures, each with the entry's identity and a `reason`
 * @returns {string[]} - One line per failed entry
 */
export const bulkErpFailureLines = (failed) => {
    return (failed ?? []).map((failure) => {
        const label = dutyEntryLabel(failure);
        const entryNumber = failure?.entry_number;
        const withNumber = entryNumber && entryNumber !== label ? `${label} (Entry No. ${entryNumber})` : label;
        return `${withNumber}: ${failure?.reason ?? BULK_ERP_BLOCK_REASONS.MISSING_INVOICE}`;
    });
};

/**
 * Headline of the bulk ERP result — how many entries went through, how many did not.
 * @param {number} successCount - Entries updated to ERP
 * @param {number} failureCount - Entries left behind
 * @returns {string} - Headline sentence
 */
export const bulkErpResultHeadline = (successCount, failureCount) => {
    const updated = `${successCount} ${successCount === 1 ? 'entry' : 'entries'} updated to ERP`;
    if (failureCount === 0) return `${updated}.`;
    return `${updated}, ${failureCount} left unchanged.`;
};

/**
 * Update ERP를 막는 안내 — Tier 필수 품목의 상세 행에 Tier가 비어 있을 때.
 * 어느 품목을 고쳐야 하는지 코드로 알려 준다(같은 품목이 여러 줄이면 한 번만).
 */
export const tierMissingUpdateBlockedMessage = (rows) => {
    const codes = [...new Set((rows ?? []).map((row) => row?.item_code).filter(Boolean))];
    return `Cannot update ERP: select a Tier for ${codes.length} item(s) first.\n\n${codes.join('\n')}`;
};

/**
 * Validates if a category name is not empty after trimming.
 * @param {string} name - The category name to validate
 * @returns {boolean} - True if the name is valid (non-empty after trim)
 */
export const validateCategoryName = (name) => {
    const trimmed = name?.trim() ?? '';
    return trimmed.length > 0;
};

/**
 * Checks if a name already exists in the items list (case-insensitive).
 * @param {Array} items - Array of objects to check for duplicates
 * @param {string} name - The name to check for duplicates
 * @param {string} fieldName - The field name to compare (e.g., 'category_name', 'duty_name')
 * @returns {boolean} - True if a duplicate exists
 */
export const isDuplicateName = (items, name, fieldName = 'name') => {
    const trimmedName = name?.trim().toLowerCase() ?? '';
    return items.some((row) => row[fieldName]?.toLowerCase() === trimmedName);
};

/**
 * Displays a snackbar notification using Redux dispatch.
 * @param {Function} dispatch - Redux dispatch function
 * @param {string} message - The message to display
 * @param {string} severity - The severity level ('info' | 'success' | 'warning' | 'error')
 */
export const showSnackbar = (dispatch, message, severity = 'info') => {
    dispatch(
        setSnackbar({
            open: true,
            message,
            severity,
        }),
    );
};
